// @flow

import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Page, Spinner } from '@shopify/polaris';

import { routes } from 'src/routes';
import { isGMVMigrationStore, isPricingChangeStore } from 'src/helpers/subscription-v3-helper';

type Props = {
  component: any,
  subscriptionV5: boolean,
  tenantApp: Object,
  migrationStores: Array<string>,
  invoiceV3: Object,
  isGuardDataSettled: boolean
};

/**
 * Route guard for the V5 pricing pages: renders `component` for stores eligible for V5 pricing,
 * and sends everyone else to the plan page.
 *
 * Eligibility is decided by isGMVMigrationStore / isPricingChangeStore, which read data fetched
 * after the first render (migrationStores and invoiceV3, defaulting to [] and {}). Until those
 * settle, a non-subscriptionV5 store looks ineligible - so deciding immediately produces a false
 * negative. Inside an App Bridge modal content iframe that is not an edge case: main.jsx remounts
 * with a cold store every time the modal opens, so the guard would fail on every open, and the
 * redirect - a react-router navigation within the iframe - would render the plan page inside the
 * fullscreen modal. Hold the decision until the data can actually answer it.
 */
const V5PricingRouteGuard = ({
  component: Component,
  subscriptionV5,
  tenantApp,
  migrationStores,
  invoiceV3,
  isGuardDataSettled,
  // Everything left in routeProps is forwarded to the page component, so it must be the route's
  // own props (match / location / history) only - see the empty mapDispatchToProps below.
  ...routeProps
}: Props) => {
  if (!subscriptionV5 && !isGuardDataSettled) {
    return (
      <Page>
        <div className="BC-AppLoadingPage BC-AppLoadingPage--fullScreen">
          <Spinner accessibilityLabel={'loading'} size="large" />
        </div>
      </Page>
    );
  }

  const isEligible =
    subscriptionV5 ||
    isGMVMigrationStore(tenantApp, migrationStores) ||
    isPricingChangeStore(tenantApp, invoiceV3);

  if (!isEligible) {
    return <Redirect to={routes.subscription.plan()} />;
  }

  return <Component {...routeProps} />;
};

const mapStateToProps = state => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    tenantApp,
    subscriptionV5: _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false),
    migrationStores: _.get(state.subscriptionV5, 'migrationStores', []),
    invoiceV3: _.get(state.subscriptionV3, 'invoice', {}),
    // Both fetches must have resolved (either way) before eligibility can be judged.
    isGuardDataSettled:
      _.get(state.subscriptionV5, 'getMigrationStoresSettled', false) &&
      _.get(state.subscriptionV3, 'getInvoiceSettled', false)
  };
};

// Object form (rather than null) so connect does not inject a `dispatch` prop, which would
// otherwise be spread onto the page component along with the route props.
export default connect(mapStateToProps, {})(V5PricingRouteGuard);
